import {IDefaultVOption, IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import MappingOrigin from "~/models/mapping/mappingOrigin";
import ModelMapping from "~/models/mapping/modelMapping";
import {IOriginWrapper, TMappingOriginType} from "~/interfaces/mappingInterfaces";
import ModelMappingValueOrigin from "~/models/mapping/modelMappingValueOrigin";

/**
 * Class representing the interface between a ModelMapping and the model of interest (e.g. form, object model, salesforce)
 */
export default abstract class ModelMappingOrigin<T = any, MO extends MappingOrigin = MappingOrigin> {
  abstract origin: T
  get model(): any {
    return this.origin
  }
  get modelIdentifier(): string {
    return this.identifier
  }
  abstract identifier: string
  abstract title: string

  get modelTitle(): string {
    return this.title
  }

  /**
   * Used as the options on the "source" side of the mapping
   */
  abstract values: any

  /**
   * Used as the options on the "destination" side of the mapping
   */
  abstract valueOptions(source: ModelMappingValueOrigin): IDefaultVOption[] | IVOptionSetLocal[]

  /**
   * By default, value options are considered loaded and should be able to be found
   * See subclass overrides for situation where this may not be the case (@see ModelMappingOriginRelationIdentifier)
   * @param source
   */
  valueOptionsLoaded(source: ModelMappingValueOrigin): boolean {
    return true
  }

  /**
   * By default, fetch values do not need to occur as they will already be on front end
   * See subclass overrides for situation where this may not be the case (@see ModelMappingOriginRelationIdentifier)
   *
   * @param source
   */
  async fetchValueOptions(source: ModelMappingValueOrigin){
    return Promise.resolve()
  }

  abstract uploaderValueOptions: IDefaultVOption[];
  abstract isSingle: boolean

  /**
   * Can the model mapping type, when mapping is run, supply a string representing a record ID
   */
  canMatchUpdateRelationship: boolean = false

  /**
   * Can the model mapping type, when mapping is run, consume a string representing a record ID and establish
   * and relationship with it?
   */
  canConsumeRecordId: boolean = false

  constructor(protected id: string, protected modelMapping: ModelMapping, protected mappingOrigin: MO) {
  }

  abstract searchValues(query?: string, options?: { filters?: ((value: string) => boolean)[], showUnmappedOnly?: boolean }): Partial<this['values']>

  abstract mapValueToOption(value: this['values'][number]): IDefaultVOption

  // @return the model that will be used in formulas & in the dynamic fields tree
  get modelForFormula(): any {
    return this.origin
  }

  mappingOriginIs(...types: Array<TMappingOriginType>): boolean {
    return types.includes(this.mappingOrigin.type);
  }

  get hasOrigin(): boolean {
    return Boolean(this.origin)
  }

  // ******************** NESTED MODEL MAPPING ******************** //
  get sourceCanHaveNestedModelMappings(): boolean {
    return this.nestedModelMappingSourceOptions().length > 0
  }

  /**
   * Right now this is always true as the final determination will be dictated by the source
   * selected in the create model mapping modal.
   * Being true here allows the add nested model mapping dropdown option to appear
   */
  get destinationCanHaveNestedModelMappings(): boolean {
    return true
  }

  /**
   * Likely want to override this is subclasses
   */
  nestedModelMappingSourceOptions(): IOriginWrapper[] {
    return []
  }

  nestedModelMappingDestinationOptions(source: IOriginWrapper): IOriginWrapper[] {
    return []
  }

  get hasRelations(): boolean  {
    return false
  }

  /**
   * Get the relations for this model
   *
   * Given object model relations are setup up like Person -> Game -> Character
   *   A nested model mapping be a nesting inside a "Game" usage.
   *   calling this method would fetch the object model relations (and their related object models)
   *   and attached them to the "Game" object model
   *   Then #buildRelationIdentifiers can be used to construct them for the this model mapping's
   *   source or destination which mimics the data structure of a mapping.
   */
  async fetchRelations() {
  }

  /**
   * Useful after fetchRelations has been called
   * This method should build RelationIdentifiers on this model mapping's source or destination
   * which should mimic the source/destination data structure on the mapping.
   */
  buildRelationIdentifiers(...args) {
  }

  // ******************** NESTED MODEL MAPPING END ******************** //
}